import {memo} from 'react';
import type {MouseEventHandler, ReactNode} from 'react';
import Link from 'next/link';
import Icon from '@/components/atoms/Icon';
import styles from './ArrowButton.module.scss';

export type ArrowButtonVariant =
    | 'primary'
    | 'primaryCompact'
    | 'primaryCompactFlat'
    | 'orange'
    | 'orangeLarge'
    | 'outlineLight'
    | 'light'
    | 'outlinePrimary'
    | 'navy';

type ArrowIcon = 'ArrowForward' | 'ArrowOutward' | 'NorthEast';

interface CommonProps {
    children: ReactNode;
    variant?: ArrowButtonVariant;
    /** Trailing arrow glyph; `null` for none. */
    icon?: ArrowIcon | null;
    /** `md` 20px arrow (default), `sm` 18px. */
    iconSize?: 'md' | 'sm';
    /** The arrow nudges right in a loop. */
    bounce?: boolean;
    /** A light band sweeps across the button. Off by default for `outlinePrimary` and `navy`. */
    shine?: boolean;
    /** Leading icon, e.g. a play glyph. */
    startIcon?: ReactNode;
    className?: string;
    'aria-label'?: string;
}

interface LinkProps extends CommonProps {
    /** Renders a real link (crawlable); `target="_blank"` gets `rel="noopener noreferrer"` unless `rel` is given. */
    href: string;
    target?: '_blank';
    rel?: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
}

interface ButtonProps extends CommonProps {
    href?: undefined;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    type?: 'button' | 'submit';
}

export type ArrowButtonProps = LinkProps | ButtonProps;

const NO_SHINE: ReadonlySet<ArrowButtonVariant> = new Set<ArrowButtonVariant>(['outlinePrimary', 'navy']);

/** The site's call-to-action button: label plus trailing arrow, as a <button> (onClick) or a link (href). */
const ArrowButton = (props: ArrowButtonProps) => {
    const {children, variant = 'primary', icon = 'ArrowForward', iconSize = 'md', bounce = true, startIcon, className} = props;
    const shine = props.shine ?? !NO_SHINE.has(variant);
    const classes = [
        styles.root,
        styles[variant],
        shine && styles.shine,
        bounce && styles.bounce,
        iconSize === 'sm' && styles.iconSm,
        className
    ]
        .filter(Boolean)
        .join(' ');

    const content = (
        <>
            {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
            {children}
            {icon && (
                <span className={styles.endIcon}>
                    <Icon name={icon} />
                </span>
            )}
        </>
    );

    if (props.href !== undefined) {
        const {href, target, onClick} = props;
        const rel = props.rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);
        return (
            <Link href={href} target={target} rel={rel} className={classes} onClick={onClick} aria-label={props['aria-label']}>
                {content}
            </Link>
        );
    }

    return (
        <button type={props.type ?? 'button'} className={classes} onClick={props.onClick} aria-label={props['aria-label']}>
            {content}
        </button>
    );
};

export default memo(ArrowButton);
